import { useState } from "react";
import { useParams } from "react-router-dom";

import promo from "../../components/platform-pages/simulation/datasets/promoCompany.json";
import brightstone from "../../components/platform-pages/simulation/datasets/brightstone.json";
import ridgeway from "../../components/platform-pages/simulation/datasets/ridgeway.json";

import {
  aggregateTransactionsByMonth,
  aggregateOfficeTransactions,
  aggregateEmployeeTransactions,
  aggregateVendorTransactions
} from "../components/emissions";
import { SimulationContext } from "./simulationContext";

const { formatDate } = require("@aclymatepackages/formatters");

const convertSimulationDates = (data) => {
  if (Array.isArray(data)) {
    return data.map(convertSimulationDates);
  }

  if (data instanceof Date) {
    return data;
  }

  if (data && typeof data === "object") {
    return Object.fromEntries(
      Object.entries(data).map(([k, v]) => [k, convertSimulationDates(v)])
    );
  }

  if (
    typeof data === "string" &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(data)
  ) {
    return new Date(data);
  }

  return data;
};

const initializeAggregatedOffice = (offices, transactions) => {
  const aggregatedData = convertSimulationDates(
    aggregateOfficeTransactions(transactions)
  );

  return offices.map((office) => ({
    ...office,
    aggregatedTransactions: aggregatedData[office.id] || []
  }));
};

const normalizeDates = (companyData) => {
  const lastDay = companyData?.lastDay || new Date("2025-01-01T18:51:02.000Z");
  const today = new Date();
  const difference = today - lastDay;

  const normalizeDate = (date) => new Date(date.getTime() + difference);

  const normalizeAllDates = (obj) => {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (obj instanceof Date) {
      return normalizeDate(obj);
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => normalizeAllDates(item));
    }

    if (typeof obj === "object") {
      return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key] = normalizeAllDates(value);
        return acc;
      }, {});
    }

    return obj;
  };

  return normalizeAllDates(companyData);
};

export const SimulationContextProvider = ({ children }) => {
  const { companyName } = useParams();
  const companies = {
    promo,
    brightstone,
    ridgeway
  };

  const companyDataSet = companies[companyName] || "invalid company";

  const [accountData, setAccountData] = useState(
    () => normalizeDates(convertSimulationDates(companyDataSet)) || {}
  );

  const [transactions, setTransactions] = useState(
    accountData.transactions || []
  );
  const [emissions, setEmissions] = useState(accountData.emissions || []);
  const [employees, setEmployees] = useState(accountData.employees || []);
  const [offices, setOffices] = useState(
    initializeAggregatedOffice(accountData.offices || [], transactions)
  );
  const [events, setEvents] = useState(accountData.events || []);
  const [vendorRelationships, setVendorRelationships] = useState(
    accountData["vendor-relationships"] || []
  );
  const [allAggregatedTransactions, setAllAggregatedTransactions] = useState(
    aggregateTransactionsByMonth(transactions)
  );
  const [
    matchedVendorsAggregatedTransactions,
    setMatchedVendorsAggregatedTransactions
  ] = useState(aggregateVendorTransactions(transactions, vendorRelationships));
  const [eventsAggregatedTransactions, setEventsAggregatedTransactions] =
    useState(accountData["events-aggregated-transactions"] || []);
  const [employeesAggregatedTransactions, setEmployeesAggregatedTransactions] =
    useState(aggregateEmployeeTransactions(transactions));
  const [offsets, setOffsets] = useState(accountData.offsets || []);
  const [vehicles, setVehicles] = useState(accountData.vehicles || []);
  const [productComponents, setProductComponents] = useState([]);
  const [productLifecycleEvents, setProductLifecycleEvents] = useState([]);

  const updateAggregatedData = (aggregatedData, collection) => {
    const setterMap = {
      office: setOffices,
      employee: setEmployeesAggregatedTransactions,
      vendor: setMatchedVendorsAggregatedTransactions
    };

    const setter = setterMap[collection];

    if (collection === "employee") {
      setter(aggregatedData);
      return;
    }

    if (collection === "office") {
      const updatedCollectionData = offices.map((office) => ({
        ...office,
        aggregatedTransactions: aggregatedData[office.id] || []
      }));
      return setter(updatedCollectionData);
    }

    return setter(aggregatedData);
  };

  const updateCollection = (collection, updater) => {
    const setterMap = {
      accountData: setAccountData,
      offsets: setOffsets,
      transactions: setTransactions,
      emissions: setEmissions,
      employees: setEmployees,
      offices: setOffices,
      events: setEvents,
      vehicles: setVehicles,
      "vendor-relationships": setVendorRelationships,
      "all-aggregated-transactions": setAllAggregatedTransactions,
      "matched-vendors-aggregated-transactions":
        setMatchedVendorsAggregatedTransactions,
      "events-aggregated-transactions": setEventsAggregatedTransactions,
      "employees-aggregated-transactions": setEmployeesAggregatedTransactions,
      "product-components": setProductComponents,
      "product-lifecycle-events": setProductLifecycleEvents
    };

    const setter = setterMap[collection];
    if (!setter) {
      return;
    }

    if (collection === "transactions") {
      const newTransactions = updater(transactions)
        .filter((transaction) => !transaction.archived)
        .map((transaction) => ({
          ...transaction,
          displayName: `${formatDate(transaction.date)} ${
            transaction.subcategory
          } Emissions`,
          type: "transaction"
        }));

      const aggregatedTransactionData =
        aggregateTransactionsByMonth(newTransactions);

      const aggregatedOfficeData = aggregateOfficeTransactions(newTransactions);
      updateAggregatedData(aggregatedOfficeData, "office");

      const aggregatedEmployeeData =
        aggregateEmployeeTransactions(newTransactions);
      updateAggregatedData(aggregatedEmployeeData, "employee");

      const aggregatedVendorData = aggregateVendorTransactions(
        newTransactions,
        vendorRelationships
      );
      updateAggregatedData(aggregatedVendorData, "vendor");

      setTransactions(newTransactions);
      setAllAggregatedTransactions(aggregatedTransactionData);
      return;
    }

    return setter(updater);
  };

  return (
    <SimulationContext.Provider
      value={{
        accountData,
        offsets,
        transactions,
        emissions,
        employees,
        offices,
        events,
        vehicles,
        "vendor-relationships": vendorRelationships,
        "all-aggregated-transactions": allAggregatedTransactions,
        "matched-vendors-aggregated-transactions":
          matchedVendorsAggregatedTransactions,
        "events-aggregated-transactions": eventsAggregatedTransactions,
        "employees-aggregated-transactions": employeesAggregatedTransactions,
        "product-components": productComponents,
        "product-lifecycle-events": productLifecycleEvents,
        setAccountData,
        setTransactions,
        updateCollection
      }}
    >
      {children}
    </SimulationContext.Provider>
  );
};

export { default } from "./simulationContext";
